import { sleep } from '@m43/core';
import {
    createUseStream,
    SocketListener,
    StreamListener,
    createUseSocketStatus,
} from '@m43/sockets-mark43';
import { observer } from 'mobx-react-lite';
import React, {
    createContext,
    PropsWithChildren,
    useCallback,
    useContext,
    useEffect,
    useRef,
} from 'react';
import {
    AdditionalInfoFormViewNext,
    AdditionalInfoType,
    AdditionalInfoView,
    StreamMap,
} from '../../types';
import { AdditionalInfoStore } from './additional-info-store';
import {
    AdditionalInfoNotificationManager,
    ListDirection,
    SortDirection,
    UnreadInfo,
} from './types';
import useAdditionalInfosResource from './use-additional-infos-event-resource';
import pRetry from 'p-retry';
import { partition } from 'lodash';
import { useEnabledFeatures } from '../../../common/hooks/enabled-features/use-enabled-features';
import useAdditionalInfosBoloResource from './use-additional-infos-bolo-resource';
import { boloAdditionalInfoToView } from '../../helpers/bolo-additional-info-to-view';
import { autorun, toJS } from 'mobx';
import { getOriginallyAppliedFilters } from './helpers';

const useStream = createUseStream<StreamMap>();

const createStreamListener = <K extends Readonly<(keyof StreamMap)[]>>(
    streamListener: StreamListener<StreamMap, K>
): StreamListener<StreamMap, K> => {
    return streamListener;
};

const useSocketStatus = createUseSocketStatus<StreamMap>();

const { HISTORIC, CATCHUP } = ListDirection;
const { DESC } = SortDirection;
const EVENT_NUMBER_REGEX = /(#[\w]{3,})/;

const getListDirection = (sortDirection: SortDirection): ListDirection =>
    sortDirection === DESC ? HISTORIC : CATCHUP;

type UseAdditionalInfosParams = {
    entityId: number;
    entityNumber: string;
    isBolo?: boolean;
    eventOptions?: {
        includeEventNarratives?: boolean;
        isScheduledEvent?: boolean;
    };
    currentUserId: number;
    firstUnreadInfo?: UnreadInfo;
    initialFilter?: Array<AdditionalInfoType>;
    initialSortDirection?: SortDirection;

    onAllRead?: (id: number) => void;
    pageSize?: number;
    notificationManager?: AdditionalInfoNotificationManager;
    crossClassEventsIds?: number[];
};

type LoadPageParams = {
    cursor?: number;
    limit?: number;
    types?: Array<AdditionalInfoType>;
    listDirection?: ListDirection;
};

// TODO: CAD-21824 fix type
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const useIntegratedAdditionalInfosStore = ({
    entityId,
    entityNumber,
    currentUserId,
    firstUnreadInfo,
    eventOptions,
    initialFilter,
    initialSortDirection = DESC,
    onAllRead,
    pageSize = 100,
    notificationManager,
    isBolo,
    crossClassEventsIds = [],
}: UseAdditionalInfosParams) => {
    const includeEventNarratives = eventOptions?.includeEventNarratives ?? true;
    const isScheduledEvent = eventOptions?.isScheduledEvent ?? false;
    const noSystemMessagesByDefault =
        useEnabledFeatures()?.['filterOutSystemMessagesFromNarrativeByDefault'] ?? true;

    const storeRef = useRef<AdditionalInfoStore>();

    if (storeRef.current === undefined) {
        storeRef.current = new AdditionalInfoStore({
            filter: getOriginallyAppliedFilters(initialFilter, noSystemMessagesByDefault),
            firstUnreadInfo,
            sortDirection: initialSortDirection,
            onAllRead: () => onAllRead?.(entityId),
        });
    }

    const store = storeRef.current;

    const resource = useAdditionalInfosResource();
    const boloResource = useAdditionalInfosBoloResource();

    const streamListenerRef = useRef<StreamListener<StreamMap, 'infos'[]>>();
    const boloStreamListenerRef = useRef<StreamListener<StreamMap, 'boloAdditionalInfo'[]>>();

    const loadPage = useCallback(
        async ({
            cursor,
            // eslint-disable-next-line @typescript-eslint/prefer-ts-expect-error
            // @ts-ignore TODO fix type
            listDirection = getListDirection(store.sortDirection),
        }: LoadPageParams = {}) => {
            const data = await pRetry(
                async () => {
                    if (isScheduledEvent) {
                        const response = await resource.getScheduledEventInfos({
                            agencyEventId: entityId,
                        });
                        const data = response.data.data;
                        return {
                            additionalInfos: data?.additionalInfos ?? [],
                            pagination: { hasNextPage: false },
                            agencyEventNumberToIdMap: data?.agencyEventNumberToIdMap ?? {},
                        };
                    } else if (isBolo) {
                        if (!entityId) {
                            return {
                                additionalInfos: [],
                                pagination: { hasNextPage: false },
                                agencyEventNumberToIdMap: {},
                            };
                        }

                        const response = await boloResource.getInfosByCursor({
                            boloId: entityId,
                            limit: pageSize,
                            cursor,
                        });
                        const data = response.data.data;
                        return {
                            additionalInfos:
                                data?.boloAdditionalInfos.map((info) =>
                                    boloAdditionalInfoToView(info, entityNumber)
                                ) ?? [],
                            pagination: { hasNextPage: false },
                            agencyEventNumberToIdMap: {},
                        };
                    } else {
                        const response = await resource.getInfosByCursor({
                            agencyEventId: entityId,
                            limit: pageSize,
                            cursor,
                            direction: listDirection,
                            filter: toJS(store.filter),
                            includeEventNarratives,
                        });
                        return (
                            response.data.data ?? {
                                additionalInfos: [],
                                pagination: { hasNextPage: false },
                                agencyEventNumberToIdMap: {},
                            }
                        );
                    }
                },
                {
                    retries: 2,
                }
            );
            store.upsertInfos(data.additionalInfos);
            store.upsertAgencyEventNumberToIdMap(data.agencyEventNumberToIdMap);
            return data;
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [store, isScheduledEvent, resource, entityId, pageSize]
    );

    const reloadPage = useCallback(async () => {
        store.toggleIsPageReloading();
        store.listStore.setItems([]);
        await loadPage();
        store.toggleIsPageReloading();
    }, [store, loadPage]);

    const retryPageFetch = (): void => {
        store.setPageFetchFailed(false);
    };

    const setSortDirection = useCallback(
        (sortDirection: SortDirection) => {
            store.setSortDirection(sortDirection);
            fetchNextPage({ reset: true });
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [entityId]
    );

    const setFilter = useCallback(
        (filter: AdditionalInfoType[]) => {
            store.setFilter(filter);
            fetchNextPage({ reset: true });
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [entityId]
    );

    /**
     * Deletes an Additional Info.
     *
     * @param infoId - ID of the Additional Infos to delete.
     * @returns No value returned.
     * @throws An error if the Additional Info delete failed.
     */
    const deleteInfo = useCallback(
        async (infoId: number) => {
            try {
                if (isBolo) {
                    await boloResource.deleteInfo(infoId);
                } else {
                    await resource.deleteInfo(entityId, infoId);
                }
                store.removeInfos([infoId]);
            } catch (error) {
                throw new Error(`Error while trying to delete Additional Info id=${infoId}`);
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [entityId]
    );

    const createAdditionalInfo = useCallback(
        async (info: AdditionalInfoFormViewNext): Promise<AdditionalInfoView> => {
            const infosToUpsert: AdditionalInfoView[] = [];

            if (isBolo) {
                const result = await boloResource.createAdditionalInfo({
                    boloAdditionalInfo: {
                        text: info.text,
                        additionalInfoPriority: info.isHighPriority
                            ? 'HIGH_PRIORITY_UPDATE'
                            : 'STANDARD_UPDATE',
                    },
                    boloId: entityId,
                });
                const newView = result?.data?.data?.boloAdditionalInfoView?.boloAdditionalInfo;

                if (newView) infosToUpsert.push(boloAdditionalInfoToView(newView, entityNumber));
            } else {
                // currently additional-info creation endpoint will not return 'agencyEventNumberToIdMap'.
                // when a new additional-info is created, stream/nudge will indicate `infosToFetch`, then we call `fetchAdditionalInfosById` to get additional-infos with `agencyEventNumberToIdMap`
                // Once createAdditionalInfo return with 'agencyEventNumberToIdMap', we can call `upsertAgencyEventNumberToIdMap` immediately here
                const result = await resource.createAdditionalInfo(info);
                infosToUpsert.push(
                    result?.data?.data?.cadNextAdditionalInfoView as AdditionalInfoView
                );
            }

            store.upsertInfos(infosToUpsert);
            return infosToUpsert[0];
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [entityId]
    );

    // Start of infinite scroll logic

    const fetchNextPage = useCallback(
        async ({ reset = false, cursor = store.nextPageCursor } = {}) => {
            if (isBolo && !entityId) {
                return;
            }

            store.setIsFetchingNextPage(true);
            const shouldReset = !cursor || reset;

            if (shouldReset) store.reset();
            try {
                const { pagination, additionalInfos } = shouldReset
                    ? await loadPage()
                    : await loadPage({ cursor });
                const { hasNextPage } = pagination;

                store.setHasNextPage(hasNextPage);

                if (additionalInfos.length) {
                    store.setNextPageCursor(additionalInfos[additionalInfos.length - 1].id);
                }

                store.setPageFetchFailed(false);
            } catch (e) {
                // A failed next page load is caught and the pageFetchFailed boolean is set to
                // true. Set to true this boolean stops retrying of the fetch. Setting it to false
                // causes the fetch to try again.
                store.setPageFetchFailed(true);
            } finally {
                store.setIsFetchingNextPage(false);
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [loadPage, store, entityId]
    );

    // End of infinite scroll logic

    // Start Realtime Update logic

    const getAdditionalInfoIdsToEvict = useCallback(
        (infos: { agencyEventId: number; id: number }[], agencyEventId: number): number[] => {
            if (!Array.isArray(infos) || infos.length === 0) return [];
            return infos
                .filter((info) => {
                    const agencyEventIdMatches = info.agencyEventId === agencyEventId;
                    const listStoreHasItem = store.listStore.hasItem(info.id);
                    return agencyEventIdMatches && listStoreHasItem;
                })
                .map((info) => info.id);
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [store, entityId]
    );

    const getAdditionalInfoIdsToInsert = useCallback(
        (
            infos: AdditionalInfoView[],
            agencyEventId: number,
            filterTypes: string[]
        ): AdditionalInfoView[] => {
            if (!Array.isArray(infos) || infos.length === 0) return [];
            return infos.filter((info) => {
                const alreadyExists = !!store.listStore.getItemById(info.id);
                if (alreadyExists) return false;
                const matchesAgencyEventId =
                    info.agencyEventId === agencyEventId ||
                    (info.parentAgencyEventId && info.parentAgencyEventId === agencyEventId);
                const matchesFilter = filterTypes.includes(info.type);
                return matchesAgencyEventId && matchesFilter;
            });
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [store, entityId]
    );

    const fetchAdditionalInfosById = useCallback(
        async (infos: AdditionalInfoView[]) => {
            const response = await resource.getInfosByIds(entityId, {
                ids: infos.map((info) => info.id),
            });
            const data = response?.data?.data;
            if (data?.additionalInfos) {
                store.upsertInfos(data.additionalInfos, true);
            }
            if (data?.agencyEventNumberToIdMap) {
                store.upsertAgencyEventNumberToIdMap(data.agencyEventNumberToIdMap);
            }
        },
        [store, resource, entityId]
    );

    const shouldReloadAdditionalInfos = useCallback(
        (events: number[] | undefined, agencyEventId: number) => {
            return Array.isArray(events) && events.length > 0 && events.includes(agencyEventId);
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [entityId]
    );

    const updateStreamListener = useCallback(() => {
        streamListenerRef.current = createStreamListener({
            streamNames: ['infos'],
            onMessage: (message) => {
                if (
                    (isBolo && entityId) ||
                    shouldReloadAdditionalInfos(message.payload.reset, entityId)
                ) {
                    fetchNextPage({ reset: true });
                    return;
                }

                const infoIdsToEvict = getAdditionalInfoIdsToEvict(
                    message.payload.delete ?? [],
                    entityId
                );
                if (infoIdsToEvict.length > 0) {
                    store.removeInfos(infoIdsToEvict);
                }

                const infosToInsert = getAdditionalInfoIdsToInsert(
                    message.payload.create ?? [],
                    entityId,
                    store.filter ?? []
                ).filter((info) => !infoIdsToEvict.includes(info.id));
                const infosToAdd = infosToInsert.filter((info) => info.text);
                const infosToFetch = infosToInsert.filter(
                    (info) => !info.text || EVENT_NUMBER_REGEX.test(info.text)
                );

                if (infosToAdd.length > 0) {
                    const [ownInfos, otherInfos] = partition(
                        infosToAdd,
                        (info) => info.authorUserId === currentUserId
                    );
                    store.upsertInfos(otherInfos, true);
                    store.upsertInfos(ownInfos, false);
                }

                if (infosToFetch.length > 0) {
                    fetchAdditionalInfosById(infosToFetch);
                }
            },
        });
    }, [
        isBolo,
        entityId,
        currentUserId,
        fetchAdditionalInfosById,
        fetchNextPage,
        getAdditionalInfoIdsToInsert,
        getAdditionalInfoIdsToEvict,
        shouldReloadAdditionalInfos,
        store,
    ]);

    const updateBoloInfoStreamListener = useCallback(() => {
        boloStreamListenerRef.current = createStreamListener({
            streamNames: ['boloAdditionalInfo'],
            onMessage: (message) => {
                const { boloId, boloAdditionalInfo, boloLinkId } = message.payload;
                if (boloId !== entityId) {
                    return;
                }

                const isDelete = !boloAdditionalInfo;

                if (isDelete) {
                    store.removeInfos([boloLinkId]);
                    return;
                }

                store.upsertInfos([boloAdditionalInfoToView(boloAdditionalInfo, entityNumber)]);
            },
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [entityId, entityNumber]);

    useEffect(() => {
        reloadPage();
        updateStreamListener();
        updateBoloInfoStreamListener();
        store.setNextPageCursor(0);
        store.setHasNextPage(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [entityId]);

    useEffect(() => {
        return autorun(() => {
            const allRelevantEventsIds = [...crossClassEventsIds, entityId];

            store.listStore.setFilterDirective((item) => {
                // TODO: When an FR user is assigned to another event, there is a brief moment when `loadPage` function runs for the old assigned event,
                // which made a mix of correct additional info messages (for the new event) and incorrect ones (for the old event) be displayed in FR
                // Assigned Event page. To make sure that does not happen, we are currently using this filter directive. It is not ideal, but other
                // approaches to fix that might have the potential to cause other, maybe even worse bugs. Besides, we shall work on a better, more simple
                // approach once we move `additional-infos` from `libs` to `cad-next` and refactor its architecture.
                if (isBolo || isScheduledEvent) {
                    return true;
                }

                if (
                    item.parentAgencyEventId &&
                    allRelevantEventsIds.includes(item.parentAgencyEventId)
                ) {
                    return true;
                }

                return allRelevantEventsIds.includes(item.agencyEventId);
            });
        });
    }, [store, entityId, crossClassEventsIds, isScheduledEvent, isBolo]);

    if (streamListenerRef.current === undefined) {
        updateStreamListener();
    }

    if (boloStreamListenerRef.current === undefined) {
        updateBoloInfoStreamListener();
    }

    useStream(streamListenerRef.current);

    useStream(boloStreamListenerRef.current);

    const socketListenerRef = useRef<SocketListener<StreamMap>>();

    if (socketListenerRef.current === undefined) {
        socketListenerRef.current = {
            invokeOnInitialize: true,
            onSocketStatusChange: ({ status }) => {
                if (status === 'CONNECTED') {
                    if (!store.initialSocketConnectionMade) {
                        store.setInitialSocketConnectionMade(true);
                    }
                    fetchNextPage({ reset: true });
                }
            },
        };
    }

    useSocketStatus(socketListenerRef.current);
    // End of Realtime Update logic

    // Start of Unread Infos Logic
    const markAsRead = async (infos: AdditionalInfoView[], immediate = false): Promise<void> => {
        if (!immediate) {
            await sleep(3000);
        }
        store.clearUnreadsBasedOnOldestUnread(infos);
    };

    const managerToUse: AdditionalInfoNotificationManager = notificationManager ?? {
        hasUnread: store.hasUnread,
        unreadInfos: store.unreadInfos,
        earliestUnreadInfo: store.earliestUnreadInfo,
        markAsRead,
    };

    return {
        ...managerToUse,
        entityId,
        entityNumber,
        agencyEventNumberToIdMap: store.agencyEventNumberToIdMap,
        createAdditionalInfo,
        deleteInfo,
        editorState: store.editorState,
        fetchNextPage,
        filter: store.filter,
        getIndexById: store.listStore.getItemIndex.bind(store.listStore),
        hasNextPage: store.hasNextPage,
        initialSocketConnectionMade: store.initialSocketConnectionMade,
        isFetchingNextPage: store.isFetchingNextPage,
        isHighPriority: store.isHighPriority,
        isPageReloading: store.isPageReloading,
        list: store.listStore.data.list,
        markAsUnread: store.markAsUnread,
        pageFetchFailed: store.pageFetchFailed,
        pageSize,
        reloadPage,
        retryPageFetch,
        setFilter,
        setIsHighPriority: store.setIsHighPriority,
        setSortDirection,
        sortDirection: store.sortDirection,
        storeEditorState: store.storeEditorState,
    };
};

export const AdditionalInfosContext = createContext<
    ReturnType<typeof useIntegratedAdditionalInfosStore> | undefined
>(undefined);

// TODO: CAD-21824 fix type
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export const useAdditionalInfos = () => {
    const store = useContext(AdditionalInfosContext);
    if (!store) throw new Error("Couldn't find AdditionalInfosContext");
    return store;
};

export const AdditionalInfosProviderCore: React.FC<
    PropsWithChildren<UseAdditionalInfosParams>
> = observer(({ children, ...rest }) => {
    const store = useIntegratedAdditionalInfosStore(rest);
    return (
        <AdditionalInfosContext.Provider value={store}>{children}</AdditionalInfosContext.Provider>
    );
});
